/**
 * DEM tile maths (owner B1). Pure functions only: no DOM, no MapLibre, so they run in vitest.
 *
 * The DEM pack (M2) holds z0–9 over the region and z10–12 only inside the metro box. Where a
 * z10–12 tile is missing, src/map/memorySource.ts synthesises it from its nearest ancestor:
 *   decode terrarium → crop the child's quadrant → upsample the ELEVATIONS → re-encode terrarium.
 * Interpolating the encoded RGB would be wrong: G wraps every 256 m and B every metre, so a
 * blend of two neighbours' bytes is not the blend of their heights.
 *
 * Terrarium: elev = R·256 + G + B/256 − 32768 (metres). In 1/256 m units that is one 24-bit
 * integer q = R<<16 | G<<8 | B, elev = q/256 − 32768, which makes the round trip exact.
 */

export const TERRARIUM_OFFSET = 32768;
const Q_MAX = 0xffffff; // 24 bits of 1/256 m

export type Rgba = Uint8Array | Uint8ClampedArray;

/** Decode terrarium RGBA (length 4·n) into metres. */
export function decodeTerrarium(rgba: Rgba, out?: Float32Array): Float32Array {
  const n = rgba.length >> 2;
  const e = out ?? new Float32Array(n);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    e[i] = rgba[p] * 256 + rgba[p + 1] + rgba[p + 2] / 256 - TERRARIUM_OFFSET;
  }
  return e;
}

/** Encode metres as terrarium RGBA (alpha 255), rounded to the nearest 1/256 m. */
export function encodeTerrarium(elev: ArrayLike<number>, out?: Uint8ClampedArray<ArrayBuffer>): Uint8ClampedArray<ArrayBuffer> {
  const n = elev.length;
  const px = out ?? new Uint8ClampedArray(n * 4);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    const v = elev[i];
    let q = Math.round(((Number.isFinite(v) ? v : 0) + TERRARIUM_OFFSET) * 256);
    if (q < 0) q = 0;
    else if (q > Q_MAX) q = Q_MAX;
    px[p] = q >>> 16;
    px[p + 1] = (q >>> 8) & 255;
    px[p + 2] = q & 255;
    px[p + 3] = 255;
  }
  return px;
}

/** The ancestor of tile z/x/y at zoom az (az ≤ z), and the child's quadrant inside it. */
export function ancestorOf(z: number, x: number, y: number, az: number): { z: number; x: number; y: number; dz: number; qx: number; qy: number } {
  const dz = z - az;
  if (dz < 0) throw new RangeError(`ancestor zoom ${az} is below the tile's zoom ${z}`);
  const ax = Math.floor(x / 2 ** dz);
  const ay = Math.floor(y / 2 ** dz);
  return { z: az, x: ax, y: ay, dz, qx: x - ax * 2 ** dz, qy: y - ay * 2 ** dz };
}

export type Upsample = "bilinear" | "bicubic";

/**
 * Crop the child quadrant (qx, qy) at depth dz out of an n×n elevation grid and resample it back
 * to n×n. Pixel centres are respected: output pixel i samples the source at
 * ox + (i + 0.5)/s − 0.5 (s = 2^dz), so a 2× upsample of a ramp stays a ramp.
 *
 * bilinear  – as briefed. Near the ancestor's outer edge (half a source pixel) it extrapolates
 *             linearly instead of clamping, so the slope does not flatten along tile seams.
 * bicubic   – Catmull-Rom, C1-continuous: slopes are continuous across source pixels, so hillshade
 *             computed on the upsampled grid shows no facets. Offered for comparison.
 */
export function upsampleQuadrant(
  src: Float32Array,
  n: number,
  dz: number,
  qx: number,
  qy: number,
  method: Upsample = "bilinear",
  out: Float32Array = new Float32Array(n * n),
): Float32Array {
  if (src.length !== n * n) throw new RangeError(`source is ${src.length} samples, expected ${n}×${n}`);
  const s = 2 ** dz;
  if (!Number.isInteger(n / s) || n / s < 2) throw new RangeError(`cannot crop 1/${s} of a ${n} px tile`);
  const span = n / s;
  const ox = qx * span;
  const oy = qy * span;
  if (dz === 0) {
    out.set(src);
    return out;
  }
  // Per-column taps are the same for every row: precompute them once.
  const xs = taps(n, s, ox);
  const ys = taps(n, s, oy);
  if (method === "bilinear") {
    for (let j = 0; j < n; j++) {
      const y0 = ys.i0[j];
      const ty = ys.t[j];
      const r0 = y0 * n;
      const r1 = r0 + n;
      const o = j * n;
      for (let i = 0; i < n; i++) {
        const x0 = xs.i0[i];
        const tx = xs.t[i];
        const a = src[r0 + x0];
        const b = src[r0 + x0 + 1];
        const c = src[r1 + x0];
        const d = src[r1 + x0 + 1];
        const top = a + (b - a) * tx;
        const bot = c + (d - c) * tx;
        out[o + i] = top + (bot - top) * ty;
      }
    }
    return out;
  }
  // Catmull-Rom (a = −0.5), indices clamped at the ancestor's edge.
  const wx = cubicWeights(xs.f, n);
  const wy = cubicWeights(ys.f, n);
  for (let j = 0; j < n; j++) {
    const o = j * n;
    const jy = j * 4;
    const r = [wy.idx[jy] * n, wy.idx[jy + 1] * n, wy.idx[jy + 2] * n, wy.idx[jy + 3] * n];
    const w = [wy.w[jy], wy.w[jy + 1], wy.w[jy + 2], wy.w[jy + 3]];
    for (let i = 0; i < n; i++) {
      const ix = i * 4;
      const c0 = wx.idx[ix];
      const c1 = wx.idx[ix + 1];
      const c2 = wx.idx[ix + 2];
      const c3 = wx.idx[ix + 3];
      const u0 = wx.w[ix];
      const u1 = wx.w[ix + 1];
      const u2 = wx.w[ix + 2];
      const u3 = wx.w[ix + 3];
      let v = 0;
      for (let k = 0; k < 4; k++) {
        const row = r[k];
        v += w[k] * (u0 * src[row + c0] + u1 * src[row + c1] + u2 * src[row + c2] + u3 * src[row + c3]);
      }
      out[o + i] = v;
    }
  }
  return out;
}

function taps(n: number, s: number, o: number): { i0: Int32Array; t: Float32Array; f: Float64Array } {
  const i0 = new Int32Array(n);
  const t = new Float32Array(n);
  const f = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const fx = o + (i + 0.5) / s - 0.5;
    f[i] = fx;
    let k = Math.floor(fx);
    if (k < 0) k = 0;
    else if (k > n - 2) k = n - 2;
    i0[i] = k;
    t[i] = fx - k; // < 0 or > 1 only within half a source pixel of the edge: linear extrapolation
  }
  return { i0, t, f };
}

function cubicWeights(f: Float64Array, n: number): { idx: Int32Array; w: Float64Array } {
  const idx = new Int32Array(f.length * 4);
  const w = new Float64Array(f.length * 4);
  for (let i = 0; i < f.length; i++) {
    const k = Math.floor(f[i]);
    const t = f[i] - k;
    const t2 = t * t;
    const t3 = t2 * t;
    const ws = [-0.5 * t3 + t2 - 0.5 * t, 1.5 * t3 - 2.5 * t2 + 1, -1.5 * t3 + 2 * t2 + 0.5 * t, 0.5 * t3 - 0.5 * t2];
    for (let m = 0; m < 4; m++) {
      let c = k - 1 + m;
      if (c < 0) c = 0;
      else if (c > n - 1) c = n - 1;
      idx[i * 4 + m] = c;
      w[i * 4 + m] = ws[m];
    }
  }
  return { idx, w };
}

/** Parse "<scheme>://<name>/<z>/<x>/<y>" (anything after y, such as ".png", is ignored). */
export function parseTileUrl(url: string, scheme: string): { name: string; z: number; x: number; y: number } | null {
  const m = new RegExp(`^${scheme}://(.+)/(\\d+)/(\\d+)/(\\d+)`).exec(url);
  if (!m) return null;
  return { name: m[1], z: Number(m[2]), x: Number(m[3]), y: Number(m[4]) };
}

/** Small LRU with an entry cap and an optional byte budget. Map order = recency. */
export class Lru<K, V> {
  private map = new Map<K, V>();
  private bytes = 0;
  constructor(
    readonly maxEntries: number,
    readonly maxBytes = Infinity,
    private readonly sizeOf: (v: V) => number = () => 0,
  ) {}
  get size(): number {
    return this.map.size;
  }
  get totalBytes(): number {
    return this.bytes;
  }
  has(k: K): boolean {
    return this.map.has(k);
  }
  get(k: K): V | undefined {
    const v = this.map.get(k);
    if (v === undefined) return undefined;
    this.map.delete(k);
    this.map.set(k, v);
    return v;
  }
  set(k: K, v: V): void {
    const old = this.map.get(k);
    if (old !== undefined) {
      this.bytes -= this.sizeOf(old);
      this.map.delete(k);
    }
    this.map.set(k, v);
    this.bytes += this.sizeOf(v);
    while (this.map.size > this.maxEntries || (this.bytes > this.maxBytes && this.map.size > 1)) {
      const oldest = this.map.keys().next().value as K;
      this.bytes -= this.sizeOf(this.map.get(oldest) as V);
      this.map.delete(oldest);
    }
  }
  clear(): void {
    this.map.clear();
    this.bytes = 0;
  }
}
